import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const AppContext = createContext();

// Realistic Sample Data for immediate UI visualization
const MOCK_REPORTS = [
  {
    id: 'mock-1',
    airline: 'Southwest',
    terminal: 'Terminal B',
    checkpoint: 'TSA PreCheck',
    waitTime: 8,
    tip: 'Left lane is moving faster!',
    timestamp: new Date(Date.now() - 1000 * 60 * 12).toISOString(), // 12 mins ago
    isMock: true
  },
  {
    id: 'mock-2',
    airline: 'Delta',
    terminal: 'Terminal A',
    checkpoint: 'Standard',
    waitTime: 24,
    tip: 'Crowded due to morning rush.',
    timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(), // 45 mins ago
    isMock: true
  },
  {
    id: 'mock-3',
    airline: 'United',
    terminal: 'Terminal A',
    checkpoint: 'CLEAR',
    waitTime: 4,
    timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(), // 2 hours ago
    isMock: true
  }
];

const MOCK_PARKING = [
  {
    id: 'mock-p1',
    lot: 'Terminal B Garage',
    status: 'Busy',
    level: 'Level 3 - Full',
    handicapAvailable: true,
    timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    isMock: true
  },
  {
    id: 'mock-p2',
    lot: 'Economy Lot',
    status: 'Open',
    level: 'Section G',
    handicapAvailable: true,
    timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
    isMock: true
  }
];

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState({
    id: null,
    username: 'Guest',
    email: '',
    isLoggedIn: false,
    isAdmin: false,
    xp: 0,
    monthly_xp: 0,
    level: 'Bronze'
  });
  
  const [notifications, setNotifications] = useState([]);
  const [reports, setReports] = useState(MOCK_REPORTS); // Initialize with mocks
  const [parkingReports, setParkingReports] = useState(MOCK_PARKING); // Initialize with mocks
  const [badges, setBadges] = useState([]);
  const [userBadges, setUserBadges] = useState([]);
  const [activeWinner, setActiveWinner] = useState(null);

  const airlines = ['Southwest', 'Delta', 'American', 'United', 'Alaska', 'JetBlue', 'Frontier', 'Spirit'];

  const addNotification = (message, type = 'success') => {
    const id = Date.now();
    setNotifications(prev => [...prev.filter(n => n.message !== message), { id, message, type }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 4000);
  };

  const emergencyLogin = (email) => {
    window.isBypassActive = true;
    setUser({
      id: 'emergency-admin',
      email: email,
      username: 'Admin (Bypass)',
      isLoggedIn: true,
      isAdmin: true,
      xp: 9999,
      monthly_xp: 999,
      level: 'Platinum'
    });
    addNotification("Emergency Bypass Active", "success");
  };

  const fetchReports = async () => {
    const { data } = await supabase
      .from('reports_v1')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(20);
    
    if (data && data.length > 0) {
      setReports(data); // Replace mocks with real data if it exists
    }
  };

  const fetchParking = async () => {
    const { data } = await supabase
      .from('parking_reports_v1')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(10);
    
    if (data && data.length > 0) {
      setParkingReports(data); // Replace mocks with real data if it exists
    }
  };

  const submitReport = async (reportData) => {
    const { error } = await supabase.from('reports_v1').insert([
      { ...reportData, user_id: user.id === 'emergency-admin' ? null : user.id }
    ]);
    if (error) throw error;
    fetchReports();
  };

  const submitParkingReport = async (parkingData) => {
    const { error } = await supabase.from('parking_reports_v1').insert([
      { ...parkingData, user_id: user.id === 'emergency-admin' ? null : user.id }
    ]);
    if (error) throw error;
    fetchParking();
  };

  const syncUser = async (session) => {
    if (window.isBypassActive) return;
    if (session?.user) {
      const { data: profile } = await supabase.from('profiles_v3').select('*').eq('id', session.user.id).single();
      if (profile) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          username: profile.first_name || session.user.email.split('@')[0],
          isLoggedIn: true,
          isAdmin: profile.is_admin || session.user.email === 'jenny@saasusecasetv.com',
          xp: profile.xp || 0,
          monthly_xp: profile.monthly_xp || 0,
          level: profile.xp >= 1000 ? 'Platinum' : profile.xp >= 500 ? 'Gold' : profile.xp >= 200 ? 'Silver' : 'Bronze',
          reports_count: profile.reports_count || 0
        });
      }
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => syncUser(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => syncUser(session));
    fetchReports();
    fetchParking();
    return () => subscription.unsubscribe();
  }, []);

  return (
    <AppContext.Provider value={{
      user, notifications, reports, parkingReports, airlines, badges, userBadges, activeWinner,
      addNotification, logout: () => { window.isBypassActive = false; supabase.auth.signOut(); },
      emergencyLogin, submitReport, submitParkingReport
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);